import React, { useCallback, useEffect, useState } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'

function StatusPill({ keyRow }) {
  if (keyRow.hasDbKey) {
    const ok = keyRow.lastTestStatus === 'ok'
    const failed = keyRow.lastTestStatus === 'error'
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
        style={{
          background: ok ? '#E1F5EE' : failed ? '#FCEBEB' : '#EEF2FF',
          color: ok ? '#085041' : failed ? '#791F1F' : '#3730A3',
        }}
      >
        {ok && <CheckCircleIcon className="w-3.5 h-3.5" />}
        {failed && <ExclamationTriangleIcon className="w-3.5 h-3.5" />}
        {ok ? 'Tested OK' : failed ? 'Last test failed' : 'Stored — not tested'}
      </span>
    )
  }
  if (keyRow.hasEnvKey) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
        style={{ background: '#E6F1FB', color: '#0C447C' }}
      >
        Using env var
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: '#FAEEDA', color: '#633806' }}
    >
      Needs key
    </span>
  )
}

function KeyRow({ row, canManage, onSaved, onTested, onDeleted }) {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [msg, setMsg] = useState(null)

  const save = async () => {
    if (!value.trim() || !canManage) return
    setSaving(true)
    setMsg(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/patron-screening/keys/${encodeURIComponent(row.sourceId)}`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: value.trim(), isEnabled: true }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setValue('')
      setMsg({ kind: 'ok', text: 'Saved.' })
      onSaved?.()
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || 'Save failed' })
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    if (!canManage) return
    setTesting(true)
    setMsg(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/patron-screening/keys/${encodeURIComponent(row.sourceId)}/test`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
      )
      const json = await res.json().catch(() => ({}))
      if (json.success) {
        setMsg({ kind: 'ok', text: `Test: ${json.status} — ${json.summary || ''}` })
      } else {
        setMsg({ kind: 'err', text: json.error || json.summary || 'Test failed' })
      }
      onTested?.()
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  const del = async () => {
    if (!row.hasDbKey || !canManage) return
    if (!confirm(`Remove stored key for ${row.sourceName}?`)) return
    setDeleting(true)
    setMsg(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/patron-screening/keys/${encodeURIComponent(row.sourceId)}`),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      onDeleted?.()
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || 'Delete failed' })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="py-4 border-b border-gray-100 last:border-0">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-semibold text-gray-900">{row.sourceName}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {row.envVar ? `Env fallback: ${row.envVar}` : 'No env fallback'}
            {row.maskedValue ? ` · stored ${row.maskedValue}` : ''}
          </div>
        </div>
        <StatusPill keyRow={row} />
      </div>
      {canManage ? (
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="password"
            autoComplete="off"
            placeholder={row.hasDbKey ? 'Enter new key to rotate' : 'Paste API key'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <Button type="button" size="sm" onClick={save} disabled={saving || !value.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={test} disabled={testing}>
            {testing ? 'Testing…' : 'Test'}
          </Button>
          {row.hasDbKey && (
            <Button type="button" variant="outline" size="sm" onClick={del} disabled={deleting}>
              <TrashIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-500">View only — manage permission required to edit keys.</p>
      )}
      {msg && (
        <p className={`text-xs mt-2 ${msg.kind === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>
      )}
    </div>
  )
}

export default function ScreeningKeysPanel({ canManage = false }) {
  const [keys, setKeys] = useState([])
  const [liveSources, setLiveSources] = useState([])
  const [masterKeyConfigured, setMasterKeyConfigured] = useState(true)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const [keysRes, sourcesRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/patron-screening/keys')),
        authFetch(getLocalApiUrl('/api/patron-screening/sources')),
      ])
      const keysJson = await keysRes.json().catch(() => ({}))
      const sourcesJson = await sourcesRes.json().catch(() => ({}))
      if (!keysRes.ok || !keysJson.success) throw new Error(keysJson.error || `HTTP ${keysRes.status}`)
      setKeys(keysJson.keys || [])
      setMasterKeyConfigured(!!keysJson.masterKeyConfigured)
      setLiveSources(Array.isArray(sourcesJson?.sources) ? sourcesJson.sources : [])
    } catch (e) {
      setErr(e.message || 'Failed to load keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const freeLiveSources = liveSources.filter((s) => !s.requiresApiKey)

  return (
    <>
      {!masterKeyConfigured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-800">
            <b>Encryption master key not configured.</b> Set{' '}
            <code className="font-mono bg-amber-100 px-1 rounded">SCREENING_KEYS_MASTER_KEY</code>{' '}
            in the backend environment before storing keys.
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <KeyIcon className="w-5 h-5 text-psg-teal" />
            <h2 className="text-lg font-semibold text-psg-teal m-0">API keys</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Only <strong>live</strong> integrations that require a paid or tenant-specific API key appear here.
            Other catalog rows (ASIC, social search, etc.) are not built yet — disable them under Sources &amp; purposes.
            PEP and adverse media are handled by GBG in Profile Safe, not this tool.
          </p>
          {loading ? (
            <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
          ) : err ? (
            <div className="py-8 text-center text-red-600 text-sm">{err}</div>
          ) : keys.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">No key-based sources configured.</div>
          ) : (
            keys.map((row) => (
              <KeyRow
                key={row.sourceId}
                row={row}
                canManage={canManage}
                onSaved={load}
                onTested={load}
                onDeleted={load}
              />
            ))
          )}
          {freeLiveSources.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Live — no API key</h3>
              <p className="text-xs text-gray-500 mb-3">
                These call public APIs when enabled in the catalog and marked <strong>Live</strong> in a scan.
              </p>
              <ul className="space-y-2">
                {freeLiveSources.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-2 text-sm text-gray-700">
                    <span>{s.name}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.ready ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                      {s.ready ? 'Ready' : 'Unavailable'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
